import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { 
  fetchNotifications, 
  markNotificationAsRead, 
  markAllNotificationsAsRead,
  deleteNotification,
  deleteSelectedNotifications,
  deleteAllNotifications,
  requestPushPermission
} from "../services/notificationService";
import { useNotificationSound } from "./useNotificationSound";

export const useNotifications = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isUserActive, setIsUserActive] = useState(true);
  const { playSound } = useNotificationSound();
  const [previousUnreadCount, setPreviousUnreadCount] = useState(0);
  const seenNotificationIdsRef = useRef<Set<string>>(new Set());
  const hasInitializedSoundRef = useRef(false);

  // Detectar actividad del usuario para polling adaptativo
  useEffect(() => {
    let inactivityTimer: ReturnType<typeof setTimeout>;

    const handleActivity = () => {
      setIsUserActive(true);
      clearTimeout(inactivityTimer);
      
      // Marcar como inactivo después de 2 minutos sin actividad
      inactivityTimer = setTimeout(() => {
        setIsUserActive(false);
      }, 120000);
    };

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('scroll', handleActivity);

    // Iniciar timer
    handleActivity();

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      clearTimeout(inactivityTimer);
    };
  }, []);

  // Polling adaptativo: 10s cuando activo, 30s cuando inactivo
  const pollingInterval = isUserActive ? 10000 : 30000;

  const { data: notifications, isLoading, error } = useQuery({
    queryKey: ["notifications", user?.id],
    queryFn: () => user ? fetchNotifications(user.id) : Promise.resolve([]),
    enabled: !!user,
    refetchInterval: pollingInterval,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 5000,
  });

  // Real-time subscription para notificaciones instantáneas
  useEffect(() => {
    if (!user) return;

    console.log('🔔 Suscribiéndose a notificaciones en tiempo real para:', user.id);

    const channel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('🔔 Nueva notificación recibida:', payload.new);
          queryClient.invalidateQueries({ queryKey: ["notifications", user.id] });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('🔔 Notificación actualizada:', payload.new);
          queryClient.invalidateQueries({ queryKey: ["notifications", user.id] });
        }
      )
      .subscribe((status) => {
        console.log('🔔 Estado de suscripción:', status);
      });

    return () => {
      console.log('🔔 Desuscribiéndose de notificaciones');
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  const notificationsKey = ["notifications", user?.id] as const;

  // Actualiza el cache local marcando como leídas las notificaciones indicadas.
  // Si `ids` es null, marca todas.
  const applyReadToCache = (ids: string[] | null) => {
    queryClient.setQueryData(notificationsKey, (old: any) => {
      if (!Array.isArray(old)) return old;
      return old.map((n: any) =>
        !ids || ids.includes(n.id) ? { ...n, read: true } : n
      );
    });
  };

  const markAsRead = useMutation({
    mutationFn: markNotificationAsRead,
    onMutate: async (notificationId: string) => {
      await queryClient.cancelQueries({ queryKey: notificationsKey });
      const previous = queryClient.getQueryData(notificationsKey);
      applyReadToCache([notificationId]);
      return { previous };
    },
    onError: (_err, _vars, context: any) => {
      if (context?.previous) queryClient.setQueryData(notificationsKey, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationsKey });
    }
  });

  const markAllAsRead = useMutation({
    mutationFn: () => user ? markAllNotificationsAsRead(user.id) : Promise.resolve(false),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: notificationsKey });
      const previous = queryClient.getQueryData(notificationsKey);
      applyReadToCache(null);
      return { previous };
    },
    onError: (_err, _vars, context: any) => {
      if (context?.previous) queryClient.setQueryData(notificationsKey, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: notificationsKey });
    }
  });


  const enablePushNotifications = async () => {
    if (!user) return { success: false, message: "User not logged in" };
    
    try {
      const success = await requestPushPermission();
      return {
        success,
        message: success
          ? "Push notifications enabled successfully"
          : "Failed to enable push notifications"
      };
    } catch (error) {
      console.error("Error enabling push notifications:", error);
      return {
        success: false,
        message: "Error enabling push notifications"
      };
    }
  };

  const deleteNotificationMutation = useMutation({
    mutationFn: (notificationId: string) => user ? deleteNotification(notificationId, user.id) : Promise.resolve(false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
    }
  });

  const deleteSelectedMutation = useMutation({
    mutationFn: (notificationIds: string[]) => user ? deleteSelectedNotifications(notificationIds, user.id) : Promise.resolve(false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
    }
  });

  const deleteAllMutation = useMutation({
    mutationFn: () => user ? deleteAllNotifications(user.id) : Promise.resolve(false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
    }
  });

  const unreadCount = notifications?.filter(n => !n.read).length || 0;

  // Reproducir sonido solo cuando aparece una notificación no leída nueva.
  // Evita falsos beeps cuando un refetch revierte temporalmente el contador.
  useEffect(() => {
    const currentNotifications = notifications || [];
    const currentIds = new Set(currentNotifications.map(n => n.id));

    if (!hasInitializedSoundRef.current) {
      seenNotificationIdsRef.current = currentIds;
      hasInitializedSoundRef.current = true;
      setPreviousUnreadCount(unreadCount);
      return;
    }

    const hasNewUnreadNotification = currentNotifications.some(n => !n.read && !seenNotificationIdsRef.current.has(n.id));

    if (hasNewUnreadNotification && unreadCount > previousUnreadCount) {
      playSound();
    }

    seenNotificationIdsRef.current = new Set([...seenNotificationIdsRef.current, ...currentIds]);
    setPreviousUnreadCount(unreadCount);
  }, [notifications, unreadCount, previousUnreadCount, playSound]);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead: (id: string) => markAsRead.mutateAsync(id).catch(() => false),
    markAllAsRead: () => markAllAsRead.mutateAsync().catch(() => false),

    deleteNotification: deleteNotificationMutation.mutate,
    deleteSelected: deleteSelectedMutation.mutate,
    deleteAll: deleteAllMutation.mutate,
    isPending: markAsRead.isPending || markAllAsRead.isPending || 
               deleteNotificationMutation.isPending || deleteSelectedMutation.isPending || deleteAllMutation.isPending,
    enablePushNotifications
  };
};
